import { Avatar } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import type { TeamMember } from '@fluenceur/types';
import type { ReactNode, Ref } from 'react';

type Member = Pick<TeamMember, 'id' | 'firstName' | 'lastName' | 'email' | 'avatarUrl' | 'role'>;
export function TeamMemberChecklist({
  members,
  selectedIds,
  onToggle,
  isLocked,
  lockedLabel,
  description,
  isDisabled,
  scrollRootRef,
  footer,
  ariaLabel,
}: {
  members: readonly Member[];
  selectedIds: ReadonlySet<string>;
  onToggle: (memberId: string) => void;
  isLocked?: (member: Member) => boolean;
  isDisabled?: (member: Member) => boolean;
  scrollRootRef?: Ref<HTMLDivElement>;
  footer?: ReactNode;
  ariaLabel?: string;
  lockedLabel?: ReactNode;
  description?: (member: Member) => ReactNode;
}) {
  return (
    <VStack
      ref={scrollRootRef}
      role={ariaLabel ? 'region' : undefined}
      aria-label={ariaLabel}
      align="stretch"
      gap={0}
      borderWidth="1px"
      borderColor="border"
      rounded="lg"
      maxH="240px"
      overflowY="auto"
    >
      {members.map((member, index) => {
        const name = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.email;
        const locked = isLocked?.(member) ?? false;
        return (
          <Flex
            key={member.id}
            as="label"
            cursor={locked || isDisabled?.(member) ? 'default' : 'pointer'}
            align="center"
            gap={3}
            px={3}
            py={2}
            borderBottomWidth={index === members.length - 1 ? '0' : '1px'}
            borderColor="border"
          >
            <Checkbox
              as="span"
              checked={locked || selectedIds.has(member.id)}
              disabled={locked || isDisabled?.(member)}
              onCheckedChange={() => onToggle(member.id)}
              inputProps={{ 'aria-label': name }}
            />
            <Avatar size="sm" name={name} src={member.avatarUrl ?? undefined} />
            <Box flex="1" minWidth={0}>
              <Text fontSize="sm" fontWeight="medium" truncate>
                {name}
              </Text>
              <Text fontSize="xs" color="fg.muted" truncate>
                {description?.(member) ?? member.email}
              </Text>
            </Box>
            {locked && lockedLabel && (
              <Text fontSize="xs" color="fg.muted">
                {lockedLabel}
              </Text>
            )}
          </Flex>
        );
      })}
      {footer}
    </VStack>
  );
}
